/**
 * gift-note.js
 * Farmstead Hudson Valley
 *
 * Handles the gift note block on product pages.
 * - Toggles textarea visibility on checkbox change
 * - After add to cart (via Dawn's cartUpdate pub/sub event),
 *   merges this product's gift note into cart.note without
 *   overwriting notes from other products.
 * - Works across both main-product and sticky-product-scroll sections.
 */

(function () {
  'use strict';

  // Shared note store — both section instances read/write here
  window.FarmsteadGiftNote = window.FarmsteadGiftNote || {
    message: '',
    active: false,
    productTitle: '',
  };

  /**
   * GiftNoteBlock
   * Manages one instance of the gift note block (one per section).
   */
  class GiftNoteBlock {
    constructor(el) {
      this.el = el;
      this.blockId = el.dataset.blockId;
      this.productTitle = el.dataset.productTitle || '';
      this.checkbox = el.querySelector('.gift-note__checkbox');
      this.field = el.querySelector('.gift-note__field');
      this.textarea = el.querySelector('.gift-note__textarea');

      if (!this.checkbox || !this.field || !this.textarea) return;

      this.bindEvents();
      this.syncFromGlobal();
    }

    bindEvents() {
      this.checkbox.addEventListener('change', () => {
        const checked = this.checkbox.checked;
        this.field.hidden = !checked;
        this.checkbox.setAttribute('aria-expanded', checked);

        window.FarmsteadGiftNote.active = checked;
        window.FarmsteadGiftNote.productTitle = this.productTitle;

        if (checked) {
          this.textarea.focus();
        } else {
          this.textarea.value = '';
          window.FarmsteadGiftNote.message = '';
          this.syncToOtherInstances();
        }
      });

      this.textarea.addEventListener('input', () => {
        window.FarmsteadGiftNote.message = this.textarea.value.trim();
        window.FarmsteadGiftNote.productTitle = this.productTitle;
        this.syncToOtherInstances();
      });
    }

    // Mirror state to other gift note blocks on the page (e.g. sticky scroll)
    syncToOtherInstances() {
      document.querySelectorAll('[data-gift-note-block]').forEach((block) => {
        if (block === this.el) return;
        const otherCheckbox = block.querySelector('.gift-note__checkbox');
        const otherField = block.querySelector('.gift-note__field');
        const otherTextarea = block.querySelector('.gift-note__textarea');
        if (!otherCheckbox || !otherField || !otherTextarea) return;

        otherCheckbox.checked = window.FarmsteadGiftNote.active;
        otherCheckbox.setAttribute('aria-expanded', window.FarmsteadGiftNote.active);
        otherField.hidden = !window.FarmsteadGiftNote.active;
        otherTextarea.value = window.FarmsteadGiftNote.message;
      });
    }

    syncFromGlobal() {
      if (window.FarmsteadGiftNote.active) {
        this.checkbox.checked = true;
        this.checkbox.setAttribute('aria-expanded', 'true');
        this.field.hidden = false;
        this.textarea.value = window.FarmsteadGiftNote.message;
      }
    }
  }

  /**
   * CartNoteSync
   * Listens for Dawn's cartUpdate pub/sub event and merges this
   * product's gift note into cart.note after a successful add to cart.
   *
   * Cart note format (one line per product):
   *   🎁 Gift Note for Calendula Body Oil: Happy birthday!
   *   🎁 Gift Note for Farm Honey Set: Enjoy the sweetness.
   *
   * Adding a new product appends a new line.
   * Re-adding the same product updates that product's existing line.
   */
  class CartNoteSync {
    constructor() {
      this.subscribed = false;
      this.init();
    }

    init() {
      if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.attachSubscriber();
      } else {
        document.addEventListener('DOMContentLoaded', () => {
          if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
            this.attachSubscriber();
          }
        });
      }
    }

    attachSubscriber() {
      if (this.subscribed) return;
      this.subscribed = true;

      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event?.source !== 'product-form') return;
        if (!window.FarmsteadGiftNote.active || !window.FarmsteadGiftNote.message) return;

        this.mergeNoteIntoCart(
          window.FarmsteadGiftNote.productTitle,
          window.FarmsteadGiftNote.message
        );
      });
    }

    /**
     * Fetches the current cart note, merges in the new product note,
     * then posts the updated note back to /cart/update.js.
     */
    mergeNoteIntoCart(productTitle, message) {
      fetch(window.Shopify?.routes?.root + 'cart.js')
        .then((res) => res.json())
        .then((cart) => {
          const existingNote = cart.note || '';
          const updatedNote = this.mergeProductNote(existingNote, productTitle, message);

          return fetch(window.Shopify?.routes?.root + 'cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ note: updatedNote }),
          });
        })
        .then((res) => res.json())
        .then((updatedCart) => {
          // Update visible cart note textarea if present on page
          const cartNoteTextarea = document.querySelector('#Cart-note, #CartDrawer-Note');
          if (cartNoteTextarea) {
            cartNoteTextarea.value = updatedCart.note || '';
          }
        })
        .catch((err) => {
          console.warn('[GiftNote] Failed to sync note to cart:', err);
        });
    }

    /**
     * Merges a single product's gift note into the full cart note string.
     * Finds an existing line for this product and replaces it,
     * or appends a new line if this product isn't in the note yet.
     */
    mergeProductNote(existingNote, productTitle, message) {
      const prefix = `🎁 Gift Note for ${productTitle}:`;
      const newLine = `${prefix} ${message}`;

      const lines = existingNote ? existingNote.split('\n') : [];
      const existingIndex = lines.findIndex((line) => line.startsWith(prefix));

      if (existingIndex !== -1) {
        // Replace the existing line for this product
        lines[existingIndex] = newLine;
      } else {
        // Append as a new line
        lines.push(newLine);
      }

      return lines.filter(Boolean).join('\n');
    }
  }

  // --- Init ---

  function initGiftNoteBlocks() {
    document.querySelectorAll('[data-gift-note-block]').forEach((el) => {
      if (el._giftNoteInit) return;
      el._giftNoteInit = true;
      new GiftNoteBlock(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initGiftNoteBlocks();
      new CartNoteSync();
    });
  } else {
    initGiftNoteBlocks();
    new CartNoteSync();
  }

  document.addEventListener('shopify:section:load', initGiftNoteBlocks);
})();